import type { Session } from "@/lib/api/attendance";
import type { Course } from "@/lib/api/courses";
import { namesZone } from "@/lib/seat-workspace";
import { formatInCourseZone, formatSessionWindow, zoneAbbreviation } from "@/lib/session-portal";

/** The register template for a class row: mark, code, title, the one interruption, time. */
export const SESSION_INDEX_TEMPLATE = "1.25rem 4.5rem minmax(0,1fr) auto minmax(11rem,auto)";

export interface SessionIndexRecord {
  course: Pick<Course, "id" | "code" | "title" | "timezone">;
  session: Pick<Session, "id" | "course_id" | "session_no" | "label" | "starts_at" | "ends_at" | "session_date" | "status">;
  /** Whether its time names the zone: set by `groupSessionIndexByDay` for the index it groups. Named when absent. */
  withZone?: boolean;
}

export function sessionIndexDate({ course, session }: SessionIndexRecord): string {
  return session.starts_at
    ? formatInCourseZone(session.starts_at, course.timezone ?? "UTC", {
      dateStyle: "medium", timeStyle: "short", timeZoneName: "short",
    })
    : `${session.session_date} · Time not scheduled`;
}

/**
 * Start to end in the shared syntax (`6:30 to 8:00 PM`), the zone named once
 * after it when the index spans several zones (`namesZone`, Home's rule); an
 * untimed class states only its date.
 */
export function sessionIndexTime({ course, session, withZone = true }: SessionIndexRecord): string {
  const zone = course.timezone ?? "UTC";
  if (!session.starts_at) {
    return formatInCourseZone(`${session.session_date}T00:00:00Z`, "UTC", { weekday: "short", month: "short", day: "numeric" });
  }
  return session.ends_at
    ? `${formatSessionWindow(session.starts_at, session.ends_at, zone)}${withZone ? ` ${zoneAbbreviation(session.ends_at, zone)}` : ""}`
    : formatInCourseZone(session.starts_at, zone, { hour: "numeric", minute: "2-digit", ...(withZone ? { timeZoneName: "short" as const } : {}) });
}

// Cross-course discovery is chronological, with the same UUID tie order as
// each canonical course read. Missing times stay last, ordered by date/UUID.
// No status or browser clock is used to infer relevance or completion.
export function orderSessionIndexRecords(left: SessionIndexRecord, right: SessionIndexRecord): number {
  const leftTime = left.session.starts_at;
  const rightTime = right.session.starts_at;
  if (leftTime && rightTime) return Date.parse(leftTime) - Date.parse(rightTime)
    || left.session.id.localeCompare(right.session.id);
  if (leftTime) return -1;
  if (rightTime) return 1;
  return left.session.session_date.localeCompare(right.session.session_date)
    || left.session.id.localeCompare(right.session.id);
}

export interface SessionIndexDay {
  /** The calendar day in the class's course zone, `YYYY-MM-DD`, or `unscheduled`. */
  key: string;
  /** `Yesterday`, `Today`, `Tomorrow`, `Tue, Oct 13`, or `Unscheduled`. */
  label: string;
  rows: SessionIndexRecord[];
}

const DAY_MS = 86_400_000;
const NEAR_DAY: Record<number, string> = { [-1]: "Yesterday", 0: "Today", 1: "Tomorrow" };

function zonedDay(instant: Date, timeZone?: string): { key: string; year: string } {
  const parts = (zone?: string) => new Intl.DateTimeFormat("en-US", {
    timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(instant);
  let resolved: Intl.DateTimeFormatPart[];
  try {
    resolved = parts(timeZone);
  } catch {
    resolved = parts("UTC");
  }
  const part = (type: Intl.DateTimeFormatPartTypes) => resolved.find(entry => entry.type === type)?.value ?? "";
  return { key: `${part("year")}-${part("month")}-${part("day")}`, year: part("year") };
}

/**
 * The index by day: each timed class on its calendar day in its own course
 * zone, days in calendar order, the `orderSessionIndexRecords` order inside a
 * day, and untimed classes in a last `Unscheduled` group.
 *
 * `now` only names the nearest days (Yesterday, Today, Tomorrow); it never
 * filters, ranks or infers a class's state. Today is one date for the whole
 * index so the names stay in calendar order: today in the courses' zone when
 * they share one, otherwise today where the reader is (`readerZone`, the
 * browser's own zone unless given). Each row's time names its zone only when
 * the timed classes span several (`withZone`).
 */
export function groupSessionIndexByDay(rows: SessionIndexRecord[], now: Date, readerZone?: string): SessionIndexDay[] {
  const days = new Map<string, SessionIndexRecord[]>();
  const unscheduled: SessionIndexRecord[] = [];
  const zones = new Set<string>();
  for (const row of [...rows].sort(orderSessionIndexRecords)) {
    if (!row.session.starts_at) {
      unscheduled.push(row);
      continue;
    }
    const zone = row.course.timezone ?? "UTC";
    zones.add(zone);
    const { key } = zonedDay(new Date(row.session.starts_at), zone);
    days.set(key, [...days.get(key) ?? [], row]);
  }
  const today = zonedDay(now, zones.size === 1 ? [...zones][0] : readerZone);
  const withZone = namesZone(zones);
  const grouped = [...days.entries()].sort(([left], [right]) => left.localeCompare(right)).map(([key, rawRows]) => {
    const dayRows = rawRows.map(row => ({ ...row, withZone }));
    const offset = Math.round((Date.parse(`${key}T00:00:00Z`) - Date.parse(`${today.key}T00:00:00Z`)) / DAY_MS);
    const label = NEAR_DAY[offset] ?? formatInCourseZone(dayRows[0].session.starts_at!, dayRows[0].course.timezone ?? "UTC", {
      weekday: "short", month: "short", day: "numeric", ...(key.startsWith(today.year) ? {} : { year: "numeric" }),
    });
    return { key, label, rows: dayRows };
  });
  return unscheduled.length ? [...grouped, { key: "unscheduled", label: "Unscheduled", rows: unscheduled }] : grouped;
}
